import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { productsApi } from '@/services/api';
import { Plus, Edit, Trash2, Eye, Search, ChevronLeft, ChevronRight, Loader2, Package, Box, RefreshCcw } from 'lucide-react';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow, } from '@/components/ui/Table';
import { Badge } from '@/components/ui/Badge';
import { formatCurrency } from '@/utils/utils';
//import { useAuthStore } from '@/store/authStore';
import { toast } from 'react-hot-toast';
import { Product } from '@/services/types/domain';
import { UPLOADS_URL } from "@/utils/utils";
import Modal from '@/components/ui/Modal';
import Select from '@/components/ui/Select';

const SellerProductsPage: React.FC = () => {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    //const { user } = useAuthStore();
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;
    const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
    const [isStockModalOpen, setIsStockModalOpen] = useState(false);
    const [quantityChange, setQuantityChange] = useState(0);
    const [stockType, setStockType] = useState('restock');
    const [notes, setNotes] = useState('');


    // Fetch products 
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

    const { data: products, isLoading, isError } = useQuery({
        queryKey: ['seller-products', currentPage, debouncedSearchTerm],
        queryFn: () => productsApi.getMyProducts({
            page: currentPage,
            per_page: itemsPerPage,
            //search: searchTerm
            search: debouncedSearchTerm,
        }),
        placeholderData: keepPreviousData,
        staleTime: 5 * 60 * 1000, // 5 minutos
    });
    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
            setCurrentPage(1); // opcional: reiniciar paginación al buscar
        }, 500); // 500ms de espera

        return () => {
            clearTimeout(handler); // limpia el timeout si el usuario sigue escribiendo
        };
    }, [searchTerm]);
    // Delete product mutation
    const deleteMutation = useMutation({
        mutationFn: (productId: number) => productsApi.deleteProduct(productId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['seller-products'] });
            toast.success('Producto eliminado correctamente');
        },
        onError: () => {
            toast.error('Error al eliminar el producto');
        }
    });

    // Update stock mutation
    const updateStockMutation = useMutation({
        mutationFn: ({ productId, data }: { productId: number; data: { quantity_change: number; type: string; notes: string } }) =>
            productsApi.updateProductStock(productId, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['seller-products'] });
            toast.success('Stock actualizado correctamente');
            setIsStockModalOpen(false);
            setQuantityChange(0);
            setNotes('');
        },
        onError: () => {
            toast.error('Error al actualizar el stock');
        }
    });

    const handleDelete = (productId: number) => {
        if (confirm('¿Estás seguro de eliminar este producto?')) {
            deleteMutation.mutate(productId);
        }
    };

    /*const handleStatusChange = (productId: number, newStatus: 'active' | 'inactive') => {
        toast.promise(
            productsApi.updateProduct(productId, { status: newStatus }),
            {
                loading: 'Actualizando estado...',
                success: () => {
                    return 'Estado actualizado';
                },
                error: 'Error al actualizar el estado'
            }
        );
    };*/

    const handleOpenStockModal = (product: Product) => {
        setSelectedProduct(product);
        setQuantityChange(0);
        setStockType('restock');
        setNotes('');
        setIsStockModalOpen(true);
    };

    const handleStockSubmit = () => {
        if (!selectedProduct || quantityChange === 0) return;

        updateStockMutation.mutate({
            productId: selectedProduct.id,
            data: {
                quantity_change: quantityChange,
                type: stockType,
                notes: notes
            }
        });
    };

    const totalPages = products?.meta?.last_page || 1;

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
            </div>
        );
    }

    if (isError) {
        return (
            <div className="p-4 text-center text-red-600">
                Error al cargar los productos. Intenta nuevamente.
            </div>
        );
    }

    return (
        <div className="container mx-auto px-4 py-8">
            <div className="mb-8">
                <h1 className="text-2xl font-bold flex items-center gap-2">
                    <Package className="w-6 h-6" />
                    Mis Productos
                </h1>
                <p className="text-gray-600 mt-1">
                    Gestiona todos los productos de tu tienda
                </p>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
                    <div className="relative w-full sm:w-64">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                        <Input
                            type="text"
                            placeholder="Buscar productos..."
                            className="pl-10"
                            value={searchTerm}
                            onChange={(e) => {
                                setSearchTerm(e.target.value);
                                setCurrentPage(1);
                            }}
                        />
                    </div>
                    {/**boton para recargar */}
                    <Button
                        leftIcon={<RefreshCcw className="w-4 h-4" />}
                        onClick={() =>
                            queryClient.refetchQueries({
                                queryKey: ['seller-products', currentPage, searchTerm],
                                exact: true,
                            })
                        }
                        className="w-full sm:w-auto"
                    >
                        Recargar
                    </Button>

                    <Button
                        leftIcon={<Plus className="w-4 h-4" />}
                        onClick={() => navigate('/seller/products/new')}
                        className="w-full sm:w-auto"
                    >

                        Nuevo Producto
                    </Button>
                </div>

                <div className="overflow-x-auto">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead className="w-[100px]">Imagen</TableHead>
                                <TableHead>Nombre</TableHead>
                                <TableHead>SKU</TableHead>
                                <TableHead>Categoría</TableHead>
                                <TableHead>Precio</TableHead>
                                <TableHead>Stock</TableHead>
                                <TableHead>Estado</TableHead>
                                <TableHead className="text-right">Acciones</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {products?.data?.length ? (
                                products.data.map((product: Product) => (
                                    <TableRow key={product.id}>
                                        <TableCell>
                                            {product.primary_image ? (
                                                <img
                                                    src={UPLOADS_URL + product.primary_image.image_url}
                                                    alt={product.name}
                                                    className="w-12 h-12 object-cover rounded-md"
                                                />
                                            ) : (
                                                <div className="w-12 h-12 bg-gray-100 rounded-md flex items-center justify-center">
                                                    <Package className="w-5 h-5 text-gray-400" />
                                                </div>
                                            )}
                                        </TableCell>
                                        <TableCell className="font-medium">
                                            {product.name}
                                        </TableCell>
                                        <TableCell>{product.sku}</TableCell>
                                        <TableCell>{product.category_name || '-'}</TableCell>
                                        <TableCell>{formatCurrency(product.price)}</TableCell>
                                        <TableCell>{product.stock_quantity}</TableCell>
                                        <TableCell>
                                            <Badge
                                                variant={
                                                    product.status === 'active' ? 'success' :
                                                        product.status === 'inactive' ? 'destructive' : 'secondary'
                                                }
                                            >
                                                {product.status === 'active' ? 'Activo' :
                                                    product.status === 'inactive' ? 'Inactivo' : 'Borrador'}
                                            </Badge>
                                        </TableCell>
                                        <TableCell className="text-right space-x-2">
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => navigate(`/seller/products/${product.id}`)}
                                                title="Ver en tienda"
                                            >
                                                <Eye className="w-4 h-4" />
                                            </Button>
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => navigate(`/seller/products/edit/${product.id}`)}
                                                title="Editar"
                                            >
                                                <Edit className="w-4 h-4" />
                                            </Button>
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => handleOpenStockModal(product)}
                                                title="Gestionar stock"
                                            >
                                                <Box className="w-4 h-4" />
                                            </Button>
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => handleDelete(product.id)}
                                                title="Eliminar"
                                                className="text-red-600 hover:text-red-800"
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={8} className="text-center py-8 text-gray-500">
                                        No tienes productos registrados
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </div>

                {products?.data?.length ? (
                    <div className="flex items-center justify-between mt-6">
                        <div className="text-sm text-gray-600">
                            Mostrando {(currentPage - 1) * itemsPerPage + 1} a{' '}
                            {Math.min(currentPage * itemsPerPage, products.meta?.total || 0)} de{' '}
                            {products.meta?.total || 0} productos
                        </div>
                        <div className="flex gap-2">
                            <Button
                                leftIcon={<ChevronLeft className="w-4 h-4 mr-1" />}
                                variant="outline"
                                size="sm"
                                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                disabled={currentPage === 1}
                            >
                                Anterior
                            </Button>
                            <Button
                                rightIcon={<ChevronRight className="w-4 h-4 ml-1" />}
                                variant="outline"
                                size="sm"
                                onClick={() => setCurrentPage(prev => prev + 1)}
                                disabled={currentPage >= totalPages}
                            >
                                Siguiente
                            </Button>
                        </div>
                    </div>
                ) : null}
            </div>

            {/* Stock Management Modal */}
            <Modal
                isOpen={isStockModalOpen}
                onClose={() => setIsStockModalOpen(false)}
                title={`Gestionar stock - ${selectedProduct?.name || ''}`}
            >
                {selectedProduct && (
                    <div className="space-y-4">
                        <div>
                            <p className="text-sm text-gray-600 mb-1">Stock actual: {selectedProduct.stock_quantity}</p>
                            <Input
                                type="number"
                                label="Cambio de cantidad"
                                value={quantityChange}
                                onChange={(e) => setQuantityChange(Number(e.target.value))}
                                placeholder="Ej: 100 para añadir, -50 para restar"
                            />
                        </div>
                        <div>
                            <Select
                                label="Tipo de movimiento"
                                value={stockType}
                                //onChange={(e) => setStockType(e.target.value)}             
                                onChange={(value) => setStockType(value as 'restock' | 'adjustment' | 'damaged')}

                                options={[
                                    { value: 'restock', label: 'Reabastecimiento' },
                                    { value: 'adjustment', label: 'Ajuste' },
                                    { value: 'damaged', label: 'Dañado/Pérdida' }
                                ]}
                            />
                        </div>
                        <div>
                            <Input
                                type="text"
                                label="Notas"
                                value={notes}
                                onChange={(e) => setNotes(e.target.value)}
                                placeholder="Ej: Llegada de pedido del proveedor #554-A"
                            />
                        </div>
                        <div className="flex justify-end gap-2 pt-4">
                            <Button
                                variant="outline"
                                onClick={() => setIsStockModalOpen(false)}
                            >
                                Cancelar
                            </Button>
                            <Button
                                onClick={handleStockSubmit}
                                disabled={quantityChange === 0 || updateStockMutation.isPending}
                            >
                                {updateStockMutation.isPending ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                    'Guardar cambios'
                                )}
                            </Button>
                        </div>
                    </div>
                )}
            </Modal>
        </div >
    );
};

export default SellerProductsPage;